import React from 'react'
import { useTranslation } from 'react-i18next'

// Material UI
import { makeStyles } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import CircularProgress from '@material-ui/core/CircularProgress'
import { DropzoneArea } from 'material-ui-dropzone'
import CloudUploadIcon from '@material-ui/icons/CloudUpload'

const useStyles = makeStyles((theme) => ({
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    borderRadius: 12,
    border: '1px solid rgba(0,0,0,0.08)',
    backgroundColor: '#fff',
  },
  head: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: theme.spacing(1.5),
  },
  iconTile: {
    flexShrink: 0,
    width: 40,
    height: 40,
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(35, 44, 87, 0.06)',
    color: '#232C57',
  },
  title: {
    fontSize: '0.95rem',
    fontWeight: 600,
    color: 'rgba(0,0,0,0.82)',
    lineHeight: 1.3,
  },
  scope: {
    fontSize: '0.8rem',
    color: 'rgba(0,0,0,0.5)',
    lineHeight: 1.45,
    marginTop: 2,
  },
  dropzone: {
    flex: 1,
    '& .MuiDropzoneArea-textContainer': {
      display: 'flex',
      flexDirection: 'column-reverse',
      alignItems: 'center',
    },
    '& .MuiDropzoneArea-root': {
      minHeight: 132,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 10,
      borderWidth: 2,
      borderColor: 'rgba(35, 44, 87, 0.2)',
      backgroundColor: 'rgba(35, 44, 87, 0.02)',
      transition: 'border-color 0.2s ease, background-color 0.2s ease',
      '&:hover': {
        borderColor: '#232C57',
        backgroundColor: 'rgba(35, 44, 87, 0.04)',
      },
    },
    '& .MuiDropzoneArea-text': {
      fontSize: '0.8rem',
      color: 'rgba(0,0,0,0.45)',
      padding: '0 16px',
      margin: '6px 0 0',
    },
    '& .MuiDropzoneArea-icon': {
      color: '#232C57',
      opacity: 0.35,
      width: 28,
      height: 28,
    },
  },
  busy: {
    flex: 1,
    minHeight: 132,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    borderRadius: 10,
    border: '2px solid rgba(35, 44, 87, 0.2)',
    backgroundColor: 'rgba(35, 44, 87, 0.02)',
  },
  busyText: {
    fontSize: '0.8rem',
    color: 'rgba(0,0,0,0.5)',
  },
  formats: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: theme.spacing(1.5),
  },
  format: {
    fontSize: '0.7rem',
    fontWeight: 500,
    color: 'rgba(0,0,0,0.5)',
    backgroundColor: 'rgba(0,0,0,0.04)',
    borderRadius: 6,
    padding: '3px 8px',
  },
}))

/**
 * One upload target on the plant analysis empty state: what it writes to
 * (parcel or farm), what it accepts, and the dropzone itself.
 */
export default function UploadTreesCard({
  icon,
  title,
  scope,
  formats,
  acceptedFiles,
  busy,
  busyText,
  onFiles,
  onRejected,
}) {
  const classes = useStyles()
  const { t } = useTranslation()

  return (
    <div className={classes.card}>
      <div className={classes.head}>
        <div className={classes.iconTile}>{icon}</div>
        <div>
          <Typography className={classes.title}>{title}</Typography>
          <Typography className={classes.scope}>{scope}</Typography>
        </div>
      </div>

      {busy ? (
        <div className={classes.busy}>
          <CircularProgress size={22} />
          <span className={classes.busyText}>{busyText}</span>
        </div>
      ) : (
        <div className={classes.dropzone}>
          <DropzoneArea
            Icon={CloudUploadIcon}
            acceptedFiles={acceptedFiles}
            dropzoneText={t('plants_upload.dropzone')}
            showAlerts={false}
            filesLimit={1}
            showPreviewsInDropzone={false}
            onChange={onFiles}
            onDropRejected={onRejected}
          />
        </div>
      )}

      {formats?.length > 0 && (
        <div className={classes.formats}>
          {formats.map((f) => (
            <span key={f} className={classes.format}>
              {f}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
